import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Mail, Phone, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function AppleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
    </svg>
  );
}

function ProviderButton({ onClick, disabled, isLoading, loadingKey, icon, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center gap-3 py-3 px-4 rounded-xl
        bg-white/[0.03] border border-white/[0.06]
        hover:bg-white/[0.06] hover:border-white/[0.10]
        active:scale-[0.98]
        text-white/80 hover:text-white text-sm font-medium
        transition-all duration-200
        disabled:opacity-40 disabled:pointer-events-none"
    >
      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
        {isLoading === loadingKey ? (
          <div className="w-4 h-4 border-2 border-white/20 border-t-white/80 rounded-full animate-spin" />
        ) : icon}
      </span>
      {children}
    </button>
  );
}

export default function SignInPrompt({ onClose, onSignedIn, mode = 'modal' }) {
  const { signInWithGoogle, signInWithApple, sendEmailLink, sendPhoneCode, verifyPhoneCode, error, clearError, isSignedIn, hasFirebaseConfig } = useAuth();
  const [isLoading, setIsLoading] = useState(null);
  const [showEmail, setShowEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const recaptchaRef = useRef(null);

  const handleGoogle = async () => {
    setIsLoading('google');
    clearError();
    try {
      const user = await signInWithGoogle();
      if (user) onSignedIn?.();
    } catch (e) {
      // error is set in AuthContext
    } finally {
      setIsLoading(null);
    }
  };

  const handleApple = async () => {
    setIsLoading('apple');
    clearError();
    try {
      const user = await signInWithApple();
      if (user) onSignedIn?.();
    } catch (e) {
      // error is set in AuthContext
    } finally {
      setIsLoading(null);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setIsLoading('email');
    clearError();
    try {
      const sent = await sendEmailLink(email.trim());
      if (sent) setEmailSubmitted(true);
    } catch (e) {
      // error set in context
    } finally {
      setIsLoading(null);
    }
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!phoneNumber.trim()) return;
    setIsLoading('phone');
    clearError();
    try {
      const formatted = phoneNumber.startsWith('+') ? phoneNumber : `+1${phoneNumber.replace(/\D/g, '')}`;
      const confirmation = await sendPhoneCode(formatted, 'recaptcha-container');
      setConfirmationResult(confirmation);
    } catch (e) {
      // error set in context
    } finally {
      setIsLoading(null);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!verificationCode.trim()) return;
    setIsLoading('verify');
    clearError();
    try {
      const user = await verifyPhoneCode(confirmationResult, verificationCode);
      if (user) onSignedIn?.();
    } catch (e) {
      // error set in context
    } finally {
      setIsLoading(null);
    }
  };

  const resetToMain = () => {
    setShowEmail(false);
    setShowPhone(false);
    setEmailSubmitted(false);
    setConfirmationResult(null);
    setVerificationCode('');
    clearError();
  };

  const isFullscreen = mode === 'fullscreen';
  const showingSubView = showEmail || showPhone;

  const content = (
    <motion.div
      initial={{ scale: 0.96, opacity: 0, y: 8 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      exit={{ scale: 0.96, opacity: 0, y: 8 }}
      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
      className={`relative ${
        isFullscreen
          ? 'w-full max-w-md mx-auto'
          : 'w-full max-w-sm'
      }`}
    >
      {/* Glass card */}
      <div className={`
        relative overflow-hidden rounded-2xl
        ${isFullscreen ? '' : 'bg-[#0a0a0c]/90 backdrop-blur-2xl border border-white/[0.06] shadow-[0_24px_80px_-12px_rgba(0,0,0,0.8)]'}
      `}>
        {/* Subtle gradient glow at top */}
        {!isFullscreen && (
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-px bg-gradient-to-r from-transparent via-teal-500/40 to-transparent" />
        )}

        <div className="relative p-7">
          {/* Close button */}
          {!isFullscreen && onClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-lg
                text-white/20 hover:text-white/60 hover:bg-white/[0.04] transition-all duration-200"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Header */}
          <div className="text-center mb-7">
            <h2 className="text-xl font-semibold text-white mb-1.5">Sign in to Touchstone</h2>
            <p className="text-[13px] text-white/30">Personalized AI tool recommendations</p>
          </div>

          <AnimatePresence mode="wait">
            {!showingSubView ? (
              <motion.div
                key="main"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.15 }}
              >
                {/* Primary providers */}
                <div className="space-y-2.5 mb-5">
                  <ProviderButton
                    onClick={handleGoogle}
                    disabled={!!isLoading}
                    isLoading={isLoading}
                    loadingKey="google"
                    icon={<GoogleIcon />}
                  >
                    Continue with Google
                  </ProviderButton>

                  {/* Apple — requires Apple Developer Program. Uncomment when configured. */}
                  {/* <ProviderButton
                    onClick={handleApple}
                    disabled={!!isLoading}
                    isLoading={isLoading}
                    loadingKey="apple"
                    icon={<AppleIcon />}
                  >
                    Continue with Apple
                  </ProviderButton> */}
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3 my-5">
                  <div className="flex-1 h-px bg-white/[0.04]" />
                  <span className="text-[10px] text-white/15 uppercase tracking-widest">or</span>
                  <div className="flex-1 h-px bg-white/[0.04]" />
                </div>

                {/* Secondary providers */}
                <div className="space-y-2.5">
                  <ProviderButton
                    onClick={() => { setShowEmail(true); setShowPhone(false); }}
                    disabled={!!isLoading}
                    isLoading={null}
                    loadingKey=""
                    icon={<Mail className="w-[18px] h-[18px] text-white/30" />}
                  >
                    Continue with email
                  </ProviderButton>
                  <ProviderButton
                    onClick={() => { setShowPhone(true); setShowEmail(false); }}
                    disabled={!!isLoading}
                    isLoading={null}
                    loadingKey=""
                    icon={<Phone className="w-[18px] h-[18px] text-white/30" />}
                  >
                    Continue with phone
                  </ProviderButton>
                </div>
              </motion.div>
            ) : showEmail ? (
              <motion.div
                key="email"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.15 }}
              >
                <button
                  onClick={resetToMain}
                  className="flex items-center gap-1.5 text-[11px] text-white/25 hover:text-white/50 mb-4 transition-colors"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Back
                </button>

                {emailSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-3"
                  >
                    <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-teal-500/[0.08] border border-teal-500/[0.12] flex items-center justify-center">
                      <Mail className="w-5 h-5 text-teal-400" />
                    </div>
                    <p className="text-white font-medium text-sm mb-1">Check your email</p>
                    <p className="text-white/30 text-xs mb-0.5">We sent a sign-in link to</p>
                    <p className="text-teal-400 text-xs font-medium">{email}</p>
                    <p className="text-white/15 text-[10px] mt-3">Click the link to sign in. You can close this tab.</p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleEmailSubmit} className="space-y-3">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      autoFocus
                      className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white text-sm
                        placeholder-white/20 focus:outline-none focus:border-teal-500/30 focus:bg-white/[0.04]
                        transition-all duration-200"
                    />
                    <button
                      type="submit"
                      disabled={!email.trim() || isLoading === 'email'}
                      className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-sm transition-all duration-200
                        ${email.trim() && isLoading !== 'email'
                          ? 'bg-teal-500/90 text-white hover:bg-teal-500 shadow-lg shadow-teal-500/10'
                          : 'bg-white/[0.03] text-white/20 cursor-not-allowed'
                        }
                      `}
                    >
                      {isLoading === 'email' ? (
                        <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          Send sign-in link
                          <ArrowRight className="w-3.5 h-3.5" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="phone"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.15 }}
              >
                <button
                  onClick={resetToMain}
                  className="flex items-center gap-1.5 text-[11px] text-white/25 hover:text-white/50 mb-4 transition-colors"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Back
                </button>

                {!confirmationResult ? (
                  <form onSubmit={handleSendCode} className="space-y-3">
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="+1 (555) 123-4567"
                      autoFocus
                      className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white text-sm
                        placeholder-white/20 focus:outline-none focus:border-teal-500/30 focus:bg-white/[0.04]
                        transition-all duration-200"
                    />
                    <button
                      type="submit"
                      disabled={!phoneNumber.trim() || isLoading === 'phone'}
                      className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-sm transition-all duration-200
                        ${phoneNumber.trim()
                          ? 'bg-teal-500/90 text-white hover:bg-teal-500 shadow-lg shadow-teal-500/10'
                          : 'bg-white/[0.03] text-white/20 cursor-not-allowed'
                        }
                      `}
                    >
                      {isLoading === 'phone' ? (
                        <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          Send code
                          <ArrowRight className="w-3.5 h-3.5" />
                        </>
                      )}
                    </button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyCode} className="space-y-3">
                    <p className="text-[11px] text-white/30 text-center mb-1">Enter the 6-digit code sent to your phone</p>
                    <input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      autoFocus
                      maxLength={6}
                      className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white text-sm
                        text-center tracking-[0.3em] font-mono
                        placeholder-white/15 focus:outline-none focus:border-teal-500/30 focus:bg-white/[0.04]
                        transition-all duration-200"
                    />
                    <button
                      type="submit"
                      disabled={verificationCode.length < 6 || isLoading === 'verify'}
                      className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-sm transition-all duration-200
                        ${verificationCode.length === 6
                          ? 'bg-teal-500/90 text-white hover:bg-teal-500 shadow-lg shadow-teal-500/10'
                          : 'bg-white/[0.03] text-white/20 cursor-not-allowed'
                        }
                      `}
                    >
                      {isLoading === 'verify' ? (
                        <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          Verify
                          <ArrowRight className="w-3.5 h-3.5" />
                        </>
                      )}
                    </button>
                  </form>
                )}
                <div id="recaptcha-container" ref={recaptchaRef} />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.p
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 4 }}
                className="mt-4 text-[11px] text-red-400/80 text-center"
              >
                {error}
              </motion.p>
            )}
          </AnimatePresence>

          {/* Legal */}
          <p className="mt-6 text-[10px] text-white/10 text-center leading-relaxed">
            By continuing, you agree to our{' '}
            <a href="/terms" className="text-white/20 hover:text-white/40 underline underline-offset-2 transition-colors">Terms</a>{' '}
            and{' '}
            <a href="/privacy" className="text-white/20 hover:text-white/40 underline underline-offset-2 transition-colors">Privacy Policy</a>
          </p>
        </div>
      </div>
    </motion.div>
  );

  if (isFullscreen) {
    return content;
  }

  return (
    <div className="relative">
      {content}
    </div>
  );
}
